import { NumberField, Outcome, RangeField, Step } from 'components';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useCalculator } from 'contexts/Calculator/Calculator';
import { formatNumber } from 'helpers';
import { useEffect, useRef, useState } from 'react';
import { DEFAULT_VALUES } from '../../constants';
import ConfidenceLevelInfo from './ConfidenceLevelInfo';
import PopulationSizeInfo from './PopulationSizeInfo';
import RangeOfExpectedResultsInfo from './RangeOfExpectedResultsInfo';
import RecommendedSampleSizeInfo from './RecommendedSampleSizeInfo';
import TargetMarginOfErrorInfo from './TargetMarginOfErrorInfo';

export default function Setup() {
  const [outcome, setOutcome] = useState<any>(0);
  const analytics = useAnalytics();
  const calculator = useCalculator();
  const isSampleSizeReady =
    Number.isFinite(calculator.recommendedSampleSize) && Number(calculator.recommendedSampleSize) > 0;
  const wasSampleSizeReadyRef = useRef(isSampleSizeReady);
  const skipInitialReadyEventRef = useRef(typeof window !== 'undefined' && !!window.location.hash);

  useEffect(() => {
    let newOutcome: any = '';
    if (calculator.targetMarginOfError || !(calculator.resultMaxValue || calculator.resultMinValue)) {
      newOutcome = calculator.recommendedSampleSize ? formatNumber(calculator.recommendedSampleSize) : 0;
    }
    setOutcome(newOutcome);
  }, [calculator.resultMaxValue, calculator.resultMinValue, calculator.recommendedSampleSize, calculator.targetMarginOfError]);

  useEffect(() => {
    const isReady = Number.isFinite(calculator.recommendedSampleSize) && Number(calculator.recommendedSampleSize) > 0;
    const wasReady = wasSampleSizeReadyRef.current;

    if (skipInitialReadyEventRef.current && wasReady && isReady) {
      skipInitialReadyEventRef.current = false;
    }

    if (!wasReady && isReady) {
      if (skipInitialReadyEventRef.current) {
        skipInitialReadyEventRef.current = false;
      } else {
        void analytics.trackActionEvent('sample_size_calculated', {
          confidence_level: calculator.confidenceLevel ?? null,
          population_size: calculator.populationSize ?? null,
          recommended_sample_size: Number(calculator.recommendedSampleSize),
          resource_id: 'recommended_sample_size',
          resource_type: 'tool',
          source: 'setup_step',
          target_margin_of_error: calculator.targetMarginOfError ?? null,
        });
      }
    }

    wasSampleSizeReadyRef.current = isReady;
  }, [
    analytics,
    calculator.confidenceLevel,
    calculator.populationSize,
    calculator.recommendedSampleSize,
    calculator.targetMarginOfError,
  ]);

  return (
    <Step
      className="break-after"
      description="Determine how large of an experiment or survey you need to run to get reliable results."
      exportTitle="Experiment Setup"
      ordinal={1}
      show
      title="Set Up the Experiment"
    >
      <div className="row align-items-end mb-md-5">
        <div className="col-12 mb-4 col-md-6 col-lg-5 mb-md-0 col-xl-4">
          <NumberField
            aria-label="Population Size"
            clarifier="(Population of the US)"
            data-testid="setup-population-size"
            highlight
            info={PopulationSizeInfo}
            label="Population Size"
            max={Number.POSITIVE_INFINITY}
            name="populationSize"
            placeholder="ex. 330,000,000"
            value={calculator.populationSize}
            onChange={calculator.setPopulationSize}
          />
        </div>
        <div className="col-12 mb-4 col-md-6 col-lg-5 mb-md-0 offset-lg-1 col-xl-4">
          <NumberField
            aria-label="Confidence Level"
            data-testid="setup-confidence-level"
            defaultValue={DEFAULT_VALUES.confidenceLevel}
            info={ConfidenceLevelInfo}
            label="Confidence Level"
            name="confidence"
            placeholder="ex. 95%"
            type="percentage"
            value={calculator.confidenceLevel}
            onChange={calculator.setConfidenceLevel}
          />
        </div>
      </div>
      <div className="row align-items-end mb-5">
        <div className="col-12 mb-4 col-md-6 col-lg-5 mb-md-0 col-xl-4">
          <NumberField
            aria-label="Target Margin of Error"
            data-testid="setup-target-margin-of-error"
            defaultValue={DEFAULT_VALUES.targetMarginOfError}
            info={TargetMarginOfErrorInfo}
            label="Target Margin of Error"
            name="targetMarginOfError"
            placeholder="ex. 5%"
            type="percentage"
            value={calculator.targetMarginOfError}
            onChange={calculator.setTargetMarginOfError}
          />
        </div>
        <div className="col-md-6 col-lg-5 offset-lg-1 col-xl-4">
          <RangeField
            data-testid="setup-range-expected-results"
            info={RangeOfExpectedResultsInfo}
            label="Range of Expected Results"
            name="result-range"
            optional
            placeholders={{
              min: "ex. 5%",
              max: "ex. 10%"
            }}
            values={{
              min: calculator.resultMinValue,
              max: calculator.resultMaxValue
            }}
            onChange={(values) => {
              calculator.setResultMinValue(values?.min);
              calculator.setResultMaxValue(values?.max);
            }}
          />
        </div>
      </div>
      <Outcome highlight={!!calculator.recommendedSampleSize} info={RecommendedSampleSizeInfo} large title="Recommended Sample Size">
        {outcome}
      </Outcome>
    </Step>
  );
}
